import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowViewLink } from '@/components/common/RowActions';
import { formatDisplayDateTime } from '@/utils/date';
import type { TeacherListItem } from '../types/teacherSubmission.types';
import { TeacherCategoryBadge } from './TeacherCategoryBadge';

interface GetTeacherSubmissionColumnsArgs {
  searchText: string | undefined;
}

export function getTeacherSubmissionColumns({
  searchText,
}: GetTeacherSubmissionColumnsArgs): ColumnDef<TeacherListItem>[] {
  return [
    createSerialColumn<TeacherListItem>(),
    {
      accessorKey: 'name',
      header: 'Teacher / Mobile',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.name} highlightedText={searchText} />}
          primaryClassName="font-medium"
          secondary={
            row.original.mobile ? (
              <span className="tabular-nums">
                <HighlightText text={row.original.mobile} highlightedText={searchText} />
              </span>
            ) : undefined
          }
        />
      ),
    },
    {
      accessorKey: 'designationName',
      header: 'Designation',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ row }) =>
        row.original.category ? (
          <TeacherCategoryBadge category={row.original.category} />
        ) : (
          <EmptyValue />
        ),
    },
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => <WrappingCellText>{row.original.instituteName}</WrappingCellText>,
    },
    {
      accessorKey: 'thanaName',
      header: 'Thana / District',
      cell: ({ row }) => {
        const { thanaName, districtName } = row.original;
        if (!thanaName && !districtName) return <EmptyValue />;
        return <StackedCellText primary={thanaName ?? <EmptyValue />} secondary={districtName} />;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.status === 'active'} />,
    },
    {
      accessorKey: 'appUpdatedByName',
      header: 'Submitted By',
      cell: ({ row }) =>
        row.original.appUpdatedByName ? (
          <WrappingCellText>{row.original.appUpdatedByName}</WrappingCellText>
        ) : (
          <EmptyValue />
        ),
    },
    {
      accessorKey: 'appUpdatedAt',
      header: 'Submitted At',
      cell: ({ row }) =>
        row.original.appUpdatedAt ? (
          <span className="whitespace-nowrap">
            {formatDisplayDateTime(row.original.appUpdatedAt)}
          </span>
        ) : (
          <EmptyValue />
        ),
    },
    createActionsColumn<TeacherListItem>((teacher) => (
      <RowViewLink
        to="/settings/teacher-submissions/$teacherId"
        params={{ teacherId: teacher.teacherId }}
        aria-label={`View ${teacher.name}`}
      />
    )),
  ];
}
